/**
 * component-new-arrivals.js -- Product Carousel Web Component for Mirai Online (Phase 4)
 *
 * Registered under two tag names sharing one implementation:
 *  - <new-arrivals-carousel>  New Arrivals section (original)
 *  - <product-carousel>       generic; used by the CRO Bestsellers section
 *
 * Carousel behaviour:
 *  - Native scroll-snap horizontal carousel (no JS swipe library)
 *  - Desktop prev/next arrow buttons scroll by one card width using scrollBy()
 *  - Arrow aria-disabled (not disabled attribute) preserves Tab focusability at bounds
 *  - Passive scroll listener updates arrow state on scroll
 *  - document shopify:section:load reinit (with containment self-check) for theme editor compatibility
 *  - customElements.define guard prevents DOMException on double-registration
 *
 * Security notes:
 *  - No user input is read or reflected; arrows call scrollBy() only.
 *  - No innerHTML manipulation.
 */

/* ============================================================
   PRODUCT CAROUSEL WEB COMPONENT
   ============================================================ */

class NewArrivalsCarousel extends HTMLElement {
  constructor() {
    super();
    this._track = null;
    this._prevBtn = null;
    this._nextBtn = null;
    this._scrollHandler = null;
    this._boundSectionLoad = null;
  }

  connectedCallback() {
    this._init();
    // Theme editor reinit: Shopify fires shopify:section:load on the section wrapper
    // <div> and on document, NOT on this inner custom element, and it does not propagate
    // downward. Listen on document with a containment self-check so reinit fires only when
    // THIS component's section is the one being reloaded. (The theme.liquid forwarding
    // bridge targets [data-section-type], which no element sets — so it is a no-op here.)
    this._boundSectionLoad = (event) => {
      if (event.target && event.target.contains(this)) this._reinit();
    };
    document.addEventListener('shopify:section:load', this._boundSectionLoad);
  }

  disconnectedCallback() {
    this._teardown();
    if (this._boundSectionLoad) {
      document.removeEventListener('shopify:section:load', this._boundSectionLoad);
      this._boundSectionLoad = null;
    }
  }

  _reinit() {
    this._teardown();
    this._init();
  }

  _teardown() {
    if (this._scrollHandler && this._track) {
      this._track.removeEventListener('scroll', this._scrollHandler);
    }
    // The arrow handlers were never removed here, and being fresh arrow-function
    // closures each _init() they were not deduped by addEventListener either. In
    // the theme editor, connectedCallback runs on insert and then shopify:section:load
    // fires _reinit() — so both arrows ended up double-bound and one click scrolled
    // two cards.
    if (this._prevBtn && this._boundPrev) this._prevBtn.removeEventListener('click', this._boundPrev);
    if (this._nextBtn && this._boundNext) this._nextBtn.removeEventListener('click', this._boundNext);
  }

  _init() {
    this._track = this.querySelector('[data-carousel-track]');
    this._prevBtn = this.querySelector('[data-carousel-prev]');
    this._nextBtn = this.querySelector('[data-carousel-next]');

    if (!this._track) return;

    // Bind scroll handler for listener removal symmetry
    this._scrollHandler = this._updateArrows.bind(this);
    this._track.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Stable references so _teardown() can actually remove these again.
    this._boundPrev = () => this._scroll(-1);
    this._boundNext = () => this._scroll(1);
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._boundPrev);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._boundNext);

    // Set initial arrow state
    this._updateArrows();
  }

  /**
   * Scroll the track forward or backward by one card width.
   * @param {number} direction  -1 for prev, +1 for next
   */
  _scroll(direction) {
    if (!this._track) return;

    // [data-carousel-item] is the section-agnostic hook; the class is kept as a
    // fallback so the New Arrivals markup keeps working unchanged.
    const item =
      this._track.querySelector('[data-carousel-item]') ||
      this._track.querySelector('.new-arrivals__item');
    if (!item) return;

    const cardWidth = item.offsetWidth;
    const gap = parseInt(
      getComputedStyle(this._track).getPropertyValue('gap') || '0',
      10
    );

    this._track.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: 'smooth'
    });
  }

  /**
   * Update aria-disabled and opacity on both arrow buttons based on scroll position.
   */
  _updateArrows() {
    if (!this._track) return;

    const { scrollLeft, clientWidth, scrollWidth } = this._track;
    const atStart = scrollLeft <= 4;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;

    if (this._prevBtn) {
      this._prevBtn.setAttribute('aria-disabled', String(atStart));
      this._prevBtn.style.opacity = atStart ? '0.4' : '1';
    }

    if (this._nextBtn) {
      this._nextBtn.setAttribute('aria-disabled', String(atEnd));
      this._nextBtn.style.opacity = atEnd ? '0.4' : '1';
    }
  }
}

if (!customElements.get('new-arrivals-carousel')) {
  customElements.define('new-arrivals-carousel', NewArrivalsCarousel);
}

// A constructor may only back one tag name, so the alias gets its own subclass.
if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', class ProductCarousel extends NewArrivalsCarousel {});
}
